import { Suspense } from 'react';
import { Outlet, RouteObject } from 'react-router-dom';
import { IPage } from '@/common/interfaces';
import { Spinner } from '@/components/common';
import PageContentProvider from '../../components/cms/PageContentProvider';
import { DefaultMenuPages, NoMenuPages, DigitalOfficePages, UserMenuPages } from './pages';
import { AnalyticsLayout, PageNotFound, RootLayout, SignInPage } from '../../pages';
import { WaffleFlags } from '../providers/WaffleProvider';

const toRoute = (flags: WaffleFlags) => (page: IPage): RouteObject | null => {
  const waffle = flags[page.waffleFlag as keyof WaffleFlags];
  if (waffle && !waffle.is_active) return null;

  const element = page.shouldLoadContent ? (
    <PageContentProvider page={page} />
  ) : (
    <page.component content={null} />
  );

  return { path: page.path, element, handle: page.handle };
};

const filterPages = (pages: Record<string, IPage>, flags: WaffleFlags): RouteObject[] =>
  Object.values(pages).flatMap(p => {
    const r = toRoute(flags)(p);
    return r ? [r] : [];
  });

const SuspenseOutlet = () => (
  <Suspense fallback={<Spinner fullScreen />}>
    <Outlet />
  </Suspense>
);

const withAnalytics = (children: RouteObject[]): RouteObject[] => [
  {
    element: <AnalyticsLayout />,
    children: [{ element: <SuspenseOutlet />, children }],
  },
];

export const buildAuthenticatedRoutes = (flags: WaffleFlags): RouteObject[] => [
  {
    element: <RootLayout menu="default" />,
    children: withAnalytics([
      ...filterPages(DefaultMenuPages, flags),
      {
        path: '*',
        element: <PageNotFound />,
        handle: { analytics: { pageType: 'Not found page', serviceSubcategory: 'Not found' } },
      },
    ]),
  },
  {
    element: <RootLayout menu="none" />,
    children: withAnalytics(filterPages(NoMenuPages, flags)),
  },
  {
    element: <RootLayout menu="digital-office" />,
    children: withAnalytics(filterPages(DigitalOfficePages, flags)),
  },
  {
    element: <RootLayout menu="user-menu" />,
    children: withAnalytics(filterPages(UserMenuPages, flags)),
  },
];

export const buildPublicRoutes = (isLoginInProgress: boolean): RouteObject[] => [
  { path: '/redirect', element: null },
  { path: '*', element: isLoginInProgress ? <Spinner fullScreen /> : <SignInPage /> },
];
